import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../home/components/Navbar';
import { Footer } from '../home/components/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const services = [
  {
    id: 'auto',
    icon: 'ri-car-line',
    title: 'Automotive Window Tinting',
    tagline: 'Privacy, comfort & UV protection for your vehicle.',
    image: 'https://public.readdy.ai/ai/img_res/3af3a6f4e3eeb24d444b1e8ff7642fcd.jpg',
    features: ['99% UV Ray Blocking', 'Heat Rejection up to 77%', 'Privacy & Security', 'Glare Reduction', 'Lifetime Warranty', 'Same-Day Service'],
    description: 'Our automotive window tinting service uses premium 3M, LLumar, and XPEL films to deliver superior heat rejection, UV protection, and enhanced privacy. Every job is completed in our professional facility by certified installers with 15+ years of experience.',
    shades: ['Limo (5%)', 'Super Dark (15%)', 'Dark (20%)', 'Medium (35%)', 'Light (50%)', 'Clear (70%)'],
  },
  {
    id: 'residential',
    icon: 'ri-building-2-line',
    title: 'Residential Window Tinting',
    tagline: 'Keep your home cool, private & protected.',
    image: 'https://readdy.ai/api/search-image?query=modern%20luxury%20home%20large%20floor%20to%20ceiling%20windows%20residential%20window%20tinting%20light%20filtering%20natural%20sunlight%20contemporary%20living%20space%20clean%20minimal%20decor%20Florida%20home&width=900&height=600&seq=svcpg_res_01&orientation=landscape',
    features: ['Energy Cost Savings', 'Furniture & Flooring Protection', 'Privacy Control', 'Glare Elimination', 'Security Film Available', 'Professional Install'],
    description: 'Protect your home and reduce cooling costs with our residential window film solutions. Our films block up to 99% of harmful UV rays, reduce heat gain, and protect your furniture and flooring from fading — all without sacrificing your view.',
    shades: ['Clear Security', 'Low-E Insulation', 'Solar Control', 'Privacy Mirror', 'Decorative Frosted'],
  },
  {
    id: 'marine',
    icon: 'ri-ship-line',
    title: 'Marine Window Tinting',
    tagline: 'Luxury protection for your vessel on the water.',
    image: 'https://readdy.ai/api/search-image?query=luxury%20yacht%20with%20tinted%20windows%20on%20ocean%20marine%20boat%20window%20film%20professional%20installation%20sleek%20dark%20tinted%20glass%20on%20superyacht%20clear%20blue%20water%20South%20Florida%20sunset&width=900&height=600&seq=svcpg_mar_01&orientation=landscape',
    features: ['Salt Water Resistant Films', 'Heat & Glare Reduction', 'UV Protection', 'Privacy Control', 'Non-Reflective Options', 'Expert Marine Install'],
    description: 'South Florida\'s harsh marine environment demands specialized window films. Our marine-grade tinting solutions resist salt air, UV degradation, and extreme heat — keeping your vessel\'s interior cool, private, and protected on every journey.',
    shades: ['Charcoal 15%', 'Charcoal 30%', 'Neutral 35%', 'Silver Reflective', 'Non-Reflective Dark'],
  },
  {
    id: 'ppf',
    icon: 'ri-shield-line',
    title: 'Paint Protection Film',
    tagline: 'Invisible armor for your vehicle\'s finish.',
    image: 'https://readdy.ai/api/search-image?query=paint%20protection%20film%20PPF%20being%20applied%20to%20luxury%20white%20car%20hood%20clear%20protective%20film%20installation%20automotive%20detailing%20professional%20technician%20clean%20studio%20environment%20precision%20work&width=900&height=600&seq=svcpg_ppf_01&orientation=landscape',
    features: ['Self-Healing Technology', 'Rock Chip Protection', 'Bug & Chemical Resistance', 'Maintains Resale Value', 'Virtually Invisible', '10-Year Warranty'],
    description: 'Paint Protection Film (PPF) is the ultimate defense for your vehicle\'s exterior. Our XPEL and LLumar self-healing films absorb impacts, prevent rock chips, and resist environmental contaminants — all while remaining virtually invisible on the surface.',
    shades: ['Full Front Package', 'Full Body Wrap', 'Custom Coverage', 'Clear Gloss', 'Matte Finish'],
  },
  {
    id: 'ceramic',
    icon: 'ri-drop-line',
    title: 'Ceramic Coatings',
    tagline: 'Mirror-perfect shine with nano protection.',
    image: 'https://readdy.ai/api/search-image?query=ceramic%20coating%20applied%20luxury%20sports%20car%20ultra%20glossy%20paint%20finish%20ceramic%20pro%20treatment%20red%20luxury%20vehicle%20intense%20mirror%20reflection%20showroom%20quality%20automotive%20detailing%20professional%20service&width=900&height=600&seq=svcpg_cer_01&orientation=landscape',
    features: ['9H Nano Ceramic', 'Hydrophobic Surface', 'UV Oxidation Protection', 'Scratch Resistance', 'Extreme Gloss Finish', '5-Year Warranty'],
    description: 'Our professional-grade ceramic coatings create a permanent bond with your vehicle\'s paint, delivering unmatched gloss, hydrophobic protection, and resistance to environmental damage. The result is a show-quality finish that lasts years, not weeks.',
    shades: ['Maintenance Detail', 'Single Stage', 'Dual Layer', 'Premium Full Package', 'Ceramic + PPF Combo'],
  },
];

function ServiceCard({ service, index }: { service: typeof services[0]; index: number }) {
  const { ref, isVisible } = useScrollAnimation();
  const [activeTab, setActiveTab] = useState<'features' | 'shades'>('features');

  return (
    <div
      ref={ref}
      id={service.id}
      className={`anim-hidden ${isVisible ? `animate-fade-in-up delay-${(index % 3) * 100}` : ''} grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden border border-white/5 bg-[#111111] hover:border-[#C9A96E]/30 transition-all duration-500`}
    >
      {/* Image */}
      <div className={`relative h-72 lg:h-auto overflow-hidden ${index % 2 !== 0 ? 'lg:order-2' : ''}`}>
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover object-top transition-transform duration-700 hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#111111]/60 to-transparent" />
        <div className="absolute top-6 left-6">
          <div className="w-12 h-12 flex items-center justify-center bg-[#FF6B35] rounded-2xl">
            <i className={`${service.icon} text-xl text-white`} />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`p-10 lg:p-12 flex flex-col justify-center ${index % 2 !== 0 ? 'lg:order-1' : ''}`}>
        <span className="text-xs font-bold uppercase tracking-[5px] text-[#C9A96E] mb-3 block">
          {['Automotive', 'Residential', 'Marine', 'Protection', 'Coating'][index]}
        </span>
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 tracking-tight" style={{ fontFamily: 'Sora, sans-serif' }}>
          {service.title}
        </h2>
        <p className="text-[#FF6B35] text-sm font-medium mb-5">{service.tagline}</p>
        <p className="text-white/50 text-sm leading-relaxed mb-8">{service.description}</p>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {(['features', 'shades'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab
                  ? 'bg-[#FF6B35] text-white'
                  : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white'
              }`}
            >
              {tab === 'features' ? 'Key Features' : 'Options / Shades'}
            </button>
          ))}
        </div>

        {activeTab === 'features' ? (
          <div className="grid grid-cols-2 gap-2">
            {service.features.map((f) => (
              <div key={f} className="flex items-center gap-2">
                <i className="ri-check-line text-[#C9A96E] text-sm flex-shrink-0" />
                <span className="text-white/70 text-xs">{f}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {service.shades.map((s) => (
              <span key={s} className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-white/60">
                {s}
              </span>
            ))}
          </div>
        )}

        <div className="mt-8">
          <Link
            to="/#quote"
            className="inline-flex items-center gap-2 bg-[#FF6B35] hover:bg-[#E55A2B] text-white px-6 py-3 rounded-full text-sm font-bold transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            Get a Quote <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function ServicesPage() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      {/* Page Hero */}
      <section className="pt-40 pb-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20window%20tinting%20shop%20interior%20luxury%20vehicle%20being%20serviced%20clean%20modern%20auto%20detailing%20facility%20cinematic%20lighting%20dark%20atmosphere%20professional%20automotive%20service%20center&width=1920&height=600&seq=svcpg_hero_01&orientation=landscape"
            alt="Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/70 to-[#0A0A0A]" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10" ref={ref}>
          <div className={`anim-hidden ${isVisible ? 'animate-fade-in-up' : ''}`}>
            <div className="flex items-center gap-3 mb-6">
              <Link to="/" className="text-white/40 hover:text-white text-sm transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-white/20 text-sm" />
              <span className="text-[#C9A96E] text-sm">Services</span>
            </div>
            <span className="luxury-divider" />
            <span className="text-xs font-bold uppercase tracking-[5px] text-[#C9A96E] mb-4 block">What We Offer</span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight tracking-tight mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Premium Tinting &<br /><span className="shimmer-gold">Protective Services</span>
            </h1>
            <p className="text-white/50 text-lg max-w-xl leading-relaxed">
              From luxury automotive tinting to full-body paint protection, we deliver precision craftsmanship backed by the industry's best films and a lifetime warranty.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-10">
          {services.map((service, i) => (
            <ServiceCard key={service.id} service={service} index={i} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0D0D0D]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <span className="luxury-divider mx-auto" />
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight" style={{ fontFamily: 'Sora, sans-serif' }}>
            Ready to <span className="shimmer-gold">Get Started?</span>
          </h2>
          <p className="text-white/40 mb-10 text-base">Free quotes · Same-day automotive appointments available · (561) 275-9473</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/#quote"
              className="bg-[#FF6B35] hover:bg-[#E55A2B] text-white px-8 py-4 rounded-full text-sm font-bold tracking-wide transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Get a Free Quote
            </Link>
            <a
              href="tel:+15612759473"
              className="border border-white/20 hover:border-white/40 text-white px-8 py-4 rounded-full text-sm font-semibold transition-all hover:bg-white/5 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-[#FF6B35] mr-2" />
              (561) 275-9473
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
